import * as React from "react";

import type {
  Filter,
  FilterDefinition,
} from "@/shared/components/ResourceView";
import {
  coreSessionFilters,
  CreateSessionButton,
  sessionResourceIdFilter,
  SessionsList,
} from "@/shared/components/sessions";

import { useDatasetContext } from "../useDatasetContext";

import styles from "./DatasetSessions.module.css";

/**
 * Filter-menu surface for the dataset-scoped Sessions tab. Omits
 * `dataset.dataset_id` (pinned as a scope filter) and adds
 * `device.device_id` as the remaining cross-aggregate narrowing axis.
 */
const datasetSessionsFilterDefinitions: FilterDefinition[] = [
  ...coreSessionFilters,
  {
    type: "string",
    field: "device.device_id",
    name: "Device ID",
    allowedComparators: sessionResourceIdFilter.comparators,
    icon: sessionResourceIdFilter.icon,
  },
];

/**
 * "Sessions" tab on the dataset-detail route. Renders the shared
 * `<SessionsList>` scoped to the current dataset, plus a "New session"
 * toolbar action that opens the create-session page prefilled with this
 * dataset. The scope is enforced server-side via the `dataset.dataset_id`
 * cross-aggregate filter and cannot be widened by the user.
 *
 * Mounted only when `featureFlagRoute` in `DatasetsRoutes` admits
 * `/datasets/:datasetPath/sessions` under the `release-sessions` flag; with
 * the flag off the route redirects to the dataset's Files tab.
 */
export function DatasetSessions() {
  const { datasetId } = useDatasetContext();

  const scope = React.useMemo<Filter[]>(
    () => [
      {
        type: "string",
        field: "dataset.dataset_id",
        comparator: "EQUALS",
        values: [datasetId],
      },
    ],
    [datasetId],
  );

  return (
    <SessionsList
      className={styles.sessionList}
      scope={scope}
      filterDefinitions={datasetSessionsFilterDefinitions}
      noRowsContent="No sessions in this dataset"
      actions={<CreateSessionButton datasetId={datasetId} />}
    />
  );
}
